import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { breakpoints } from 'dibs-sassy/exports/breakpoints';

import { Column } from './Column';
import { SharedInViewport } from '../../../../sharedComponents/SharedInViewport';
import { trackTileImpression } from '../helpers/discoverFeedTracking';

import styles from './Masonry.scss';

// Given an array of children elements and a number of columns, places children elements
// into their respective column group. Returns an array of arrays, where the outer array
// has a length equivalent to the number of columns. Children elements are placed in each
// inner array, left to right, one element at a time.
// ex. getColumnElementGroups({ children: [x, x, x, x, x], numColumns: 4 }) -> [[x, x], [x], [x], [x]]
const getColumnElementGroups = ({ children, numColumns }) => {
    const reduced = children.reduce((prev, curr, i) => {
        const group = i % numColumns;

        if (!prev[group]) {
            prev[group] = [];
        }

        prev[group].push(curr);

        return prev;
    }, []);

    return reduced;
};

// Given a mapping of desired number of columns per breakpoint,
// returns the optimal number of columns to display (based on screen-width)
const calcNumCols = ({ breakpointToNumColsMap }) => {
    const maxNumCols = breakpointToNumColsMap.desktop;
    const { innerWidth } = global;
    let colNum = maxNumCols;

    if (typeof innerWidth !== 'undefined') {
        const bps = Object.keys(breakpointToNumColsMap).sort((a, b) => {
            return breakpoints[a].max - breakpoints[b].max;
        });

        for (const bp of bps) {
            if (innerWidth <= breakpoints[bp].max) {
                colNum = breakpointToNumColsMap[bp];
                break;
            }
        }
    }
    return colNum;
};

const trackImpressionForRow = ({ inViewport, rowIndex, columnElementGroups }) => {
    if (inViewport) {
        const tilesForRow = columnElementGroups
            .map(columnGroup => get(columnGroup, `[${rowIndex}].props.discoveryFeedTile`))
            .filter(Boolean); // account for incomplete row

        trackTileImpression({
            tiles: tilesForRow,
            indexOffset: rowIndex * columnElementGroups.length,
        });
    }
};

export const Masonry = ({ children, numCols, breakpointToNumColsMap }) => {
    const [numColumns, setNumColumns] = useState(numCols);

    const updateNumColumns = useCallback(
        () => setNumColumns(calcNumCols({ breakpointToNumColsMap })),
        [breakpointToNumColsMap]
    );
    useEffect(() => {
        // Set optimal number of columns when component mounts
        updateNumColumns();
        // Set optimal number of columns again if screen is re-sized
        global.addEventListener('resize', updateNumColumns);

        return () => global.removeEventListener('resize', updateNumColumns);
    }, [breakpointToNumColsMap, updateNumColumns]);

    // Round column widths to nearest hundredths in case of irrational numbers
    const columnWidth = +(100 / numColumns).toFixed(2);

    if (!children || !children.length || !numColumns) {
        return null;
    }
    const columnElementGroups = getColumnElementGroups({ children, numColumns });

    return (
        <div className={styles.masonryGrid}>
            {columnElementGroups.map((columnElements, columnIndex) => (
                <Column key={columnElements[0].key} columnWidth={columnWidth}>
                    {columnElements.map((element, rowIndex) => {
                        // fire impressions for entire row when left-most column elements are in viewport
                        if (columnIndex === 0) {
                            return (
                                <SharedInViewport
                                    key={element.key}
                                    stopWhenInViewport
                                    onInViewportChange={({ inViewport }) =>
                                        trackImpressionForRow({
                                            inViewport,
                                            rowIndex,
                                            columnElementGroups,
                                        })
                                    }
                                >
                                    {element}
                                </SharedInViewport>
                            );
                        } else {
                            return element;
                        }
                    })}
                </Column>
            ))}
        </div>
    );
};

Masonry.propTypes = {
    breakpointToNumColsMap: PropTypes.object,
    children: PropTypes.array,
    numCols: PropTypes.number,
};

Masonry.defaultProps = {
    breakpointToNumColsMap: {
        desktop: 4,
        tabletPortrait: 3,
        tabletLandscape: 3,
        mobile: 1,
    },
    numCols: 4,
};
